import React, { useState, ReactNode, PropsWithChildren } from 'react';
import theme from './BudgetList.module.scss';
import { faEdit, faLock, faExternalLink, faFileContract } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BudgetItemDto, BudgetItemSource, BudgetGroupType } from '@/types';
import { CustomLink } from '@/components/navigation';
import Tags from '@/components/Tags';
import { getDisplayText } from '..';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';
import BudgetItemPrice from './BudgetItemPrice';
import { Thumbnail } from '@ygtechnologies/bygr-frontend-component-library';

interface BudgetListRowProps {
    budgetItem: BudgetItemDto;
    type: BudgetGroupType;
}

type LinkWrapperProps = PropsWithChildren<Pick<BudgetListRowProps, 'budgetItem' | 'type'> & { fallback?: ReactNode }>;

const LinkWrapper: React.FC<LinkWrapperProps> = ({ children, budgetItem, type, fallback = <>{children}</> }) => {
    if (type === BudgetGroupType.ChangeRequests) {
        return (
            <CustomLink className="link-unstyled" to={`/change-requests/${budgetItem.changeRequestId}`}>
                {children}
            </CustomLink>
        );
    }

    return <>{fallback}</>;
};
/**
 * This is only used for mobile now
 */
const BudgetListRow: React.FC<BudgetListRowProps> = ({ budgetItem, type }) => {
    const getThumbnail = () => {
        if (budgetItem.isNone) {
            return null;
        }

        if (budgetItem.source === BudgetItemSource.Option) {
            if (!budgetItem.selectedOptionImages || budgetItem.selectedOptionImages.length === 0) {
                return null;
            }

            return <Thumbnail src={getProjectCatalogProductFileThumbnailUrl(budgetItem.selectedOptionImages[0].id)} />;
        }

        return <FontAwesomeIcon className={theme.icon} icon={faFileContract} size="2x" />;
    };

    return (
        <>
            <div className={theme.budgetListRow}>
                <div className={theme.image}>{getThumbnail()}</div>

                <div className={theme.mobileWrapper}>
                    <small>{budgetItem.buyerOptionGroupName}</small>
                    <span className={theme.name}>
                        <LinkWrapper budgetItem={budgetItem} type={type}>
                            {getDisplayText(budgetItem)}
                        </LinkWrapper>
                    </span>
                    {budgetItem.description && <span className={theme.description}>{budgetItem.description}</span>}
                    {budgetItem.tags && budgetItem.tags.length > 0 && <Tags size="small" tags={budgetItem.tags} />}
                </div>

                <BudgetItemPrice budgetItem={budgetItem} className={theme.price} />

                {budgetItem.buyerOptionGroupId && (
                    <div className={theme.mobileIcon}>
                        {budgetItem.canChangeSelection ? (
                            <span style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faEdit} fixedWidth />
                            </span>
                        ) : (
                            <FontAwesomeIcon icon={faLock} fixedWidth />
                        )}
                    </div>
                )}

                <div className={theme.mobileIcon}>
                    <LinkWrapper budgetItem={budgetItem} type={type} fallback={null}>
                        <FontAwesomeIcon icon={faExternalLink} fixedWidth />
                    </LinkWrapper>
                </div>
            </div>
        </>
    );
};

export default BudgetListRow;
